import { TextField } from "@mui/material";
import { FC, useState, useEffect } from "react";
import { useShallow } from "zustand/shallow";
import { checkCollectionNameAvailability } from "../../../../apis/bacchus/collections";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useCollectionDialogStore } from "./stores/CollectionDialogStore";

export const NameInput: FC = () => {
  const { name, setName, editingCollectionId } = useCollectionDialogStore(
    useShallow((state) => ({
      name: state.name,
      setName: state.setName,
      editingCollectionId: state.editingCollectionId,
    }))
  );

  const [nameError, setNameError] = useState<string>("");
  const [isCheckingName, setIsCheckingName] = useState(false);

  const debouncedName = useDebounce(name, 500);

  useEffect(() => {
    if (!debouncedName.trim()) {
      setNameError("");
      setIsCheckingName(false);
      return;
    }

    const checkNameAvailability = async () => {
      setIsCheckingName(true);
      try {
        // In edit mode, exclude the current collection from the check
        const excludeId = editingCollectionId || undefined;
        const { isTaken } = await checkCollectionNameAvailability(
          debouncedName,
          excludeId
        );

        if (isTaken) {
          setNameError("Name is already in use");
        } else {
          setNameError("");
        }
      } catch {
        setNameError("Error checking name availability");
      } finally {
        setIsCheckingName(false);
      }
    };

    checkNameAvailability();
  }, [debouncedName, editingCollectionId]);

  return (
    <TextField
      label="Collection Name"
      placeholder=""
      value={name}
      onChange={(e) => setName(e.target.value)}
      inputProps={{ "data-testid": "collections-name-input" }}
      error={!!nameError}
      helperText={
        nameError || (isCheckingName ? "Checking name availability..." : "")
      }
      fullWidth
      variant="standard"
    />
  );
};
